import React, { useState, useMemo } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Paper,
  Card,
  CardContent,
  CardActions,
  IconButton,
  InputAdornment,
  Chip,
  Autocomplete,
} from '@mui/material';
import Add from '@mui/icons-material/Add';
import Edit from '@mui/icons-material/Edit';
import Delete from '@mui/icons-material/Delete';
import Search from '@mui/icons-material/Search';
import { Student, PricingTier, CrudPermissions, School } from '../../types';
import { useData } from '../../contexts/DataContext';
import { v4 as uuidv4 } from 'uuid';
import { StudentPreAssessedFees } from './StudentPreAssessedFees';
import { mockStudents } from '../../mock/students';
import { getActiveSchools, getSchoolById } from '../../mock/schools';

interface StudentsManagementProps {
  permissions: CrudPermissions;
}

export const StudentsManagement: React.FC<StudentsManagementProps> = ({ permissions }) => {
  const { students, setStudents, parents, preAssessedFees } = useData();
  const schools = getActiveSchools();
  const [openDialog, setOpenDialog] = useState(false);
  const [editingStudent, setEditingStudent] = useState<Student | null>(null);
  const [viewingFeesForStudent, setViewingFeesForStudent] = useState<Student | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [gradeFilter, setGradeFilter] = useState<string>('all');
  const [tierFilter, setTierFilter] = useState<string>('all');
  const [schoolFilter, setSchoolFilter] = useState<string>('all');
  const [formData, setFormData] = useState<Partial<Student>>({
    studentIdNumber: '',
    firstName: '',
    lastName: '',
    email: '',
    grade: 1,
    pricingTier: 'full',
    parentIds: [],
    schoolId: '450e8400-e29b-41d4-a716-446655440000',
  });

  const filteredStudents = useMemo(() => {
    return students.filter((student) => {
      const matchesSearch =
        student.firstName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        student.lastName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        student.email.toLowerCase().includes(searchQuery.toLowerCase());

      const matchesGrade = gradeFilter === 'all' || student.grade.toString() === gradeFilter;
      const matchesTier = tierFilter === 'all' || student.pricingTier === tierFilter;
      const matchesSchool = schoolFilter === 'all' || student.schoolId === schoolFilter;

      return matchesSearch && matchesGrade && matchesTier && matchesSchool;
    });
  }, [students, searchQuery, gradeFilter, tierFilter, schoolFilter]);

  const getSchoolName = (schoolId: string): string => {
    const school = getSchoolById(schoolId);
    return school?.name || 'Unknown School';
  };

  const getParentNames = (parentIds: string[]): string => {
    const matchedParents = parents.filter(p => parentIds.includes(p.id));
    if (matchedParents.length === 0) return 'No parents assigned';
    return matchedParents.map(p => `${p.firstName} ${p.lastName}`).join(', ');
  };

  const getStudentPreAssessedFees = (studentId: string) => {
    return preAssessedFees.filter(paf => paf.studentId === studentId && paf.status === 'pending');
  };

  const handleAdd = () => {
    setEditingStudent(null);
    setFormData({
      studentIdNumber: '',
      firstName: '',
      lastName: '',
      email: '',
      grade: 1,
      pricingTier: 'full',
      parentIds: [],
      schoolId: '450e8400-e29b-41d4-a716-446655440000',
    });
    setOpenDialog(true);
  };

  const handleEdit = (student: Student) => {
    setEditingStudent(student);
    setFormData(student);
    setOpenDialog(true);
  };

  const handleDelete = (id: string) => {
    if (window.confirm('Are you sure you want to delete this student?')) {
      setStudents(students.filter((s) => s.id !== id));
      const index = mockStudents.findIndex((s) => s.id === id);
      if (index > -1) {
        mockStudents.splice(index, 1);
      }
    }
  };

  const handleSave = () => {
    if (editingStudent) {
      const updated = students.map((s) =>
        s.id === editingStudent.id ? { ...s, ...formData } : s
      );
      setStudents(updated);
      const index = mockStudents.findIndex((s) => s.id === editingStudent.id);
      if (index > -1) {
        mockStudents[index] = { ...mockStudents[index], ...formData } as Student;
      }
    } else {
      const newStudent: Student = {
        id: uuidv4(),
        studentIdNumber: formData.studentIdNumber!,
        firstName: formData.firstName!,
        lastName: formData.lastName!,
        email: formData.email!,
        grade: formData.grade!,
        pricingTier: formData.pricingTier as PricingTier,
        parentIds: [],
        schoolId: '450e8400-e29b-41d4-a716-446655440000',
      };
      setStudents([...students, newStudent]);
      mockStudents.push(newStudent);
    }
    setOpenDialog(false);
  };

  const getPricingTierColor = (tier: PricingTier) => {
    switch (tier) {
      case 'full':
        return '#0d9488';
      case 'reduced':
        return '#f59e0b';
      case 'free':
        return '#10b981';
      default:
        return '#6b7280';
    }
  };

  const getPricingTierLabel = (tier: PricingTier) => {
    switch (tier) {
      case 'full':
        return 'Full Price';
      case 'reduced':
        return 'Reduced';
      case 'free':
        return 'Free';
      default:
        return tier;
    }
  };

  if (viewingFeesForStudent) {
    return (
      <StudentPreAssessedFees
        student={viewingFeesForStudent}
        onBack={() => setViewingFeesForStudent(null)}
      />
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Box sx={{
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between',
        alignItems: { xs: 'stretch', sm: 'center' },
        gap: 2,
        mb: 3,
      }}>
        <Typography variant="h5" fontWeight={600}>
          Students Management
        </Typography>
        {permissions.create && (
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleAdd}
            sx={{
              background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
              },
              flexShrink: 0,
            }}
          >
            Add Student
          </Button>
        )}
      </Box>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{
          display: 'flex',
          gap: 2,
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: { xs: 'stretch', md: 'center' },
        }}>
          <TextField
            placeholder="Search students..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            size="small"
            sx={{
              flex: { xs: 'none', md: 1 },
              width: { xs: '100%', md: 'auto' },
              minWidth: { md: 250 },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
          />
          <Box sx={{
            display: 'flex',
            gap: 2,
            flexDirection: { xs: 'column', sm: 'row' },
            width: { xs: '100%', md: 'auto' },
          }}>
            <FormControl size="small" sx={{ flex: { xs: 1, md: 'none' }, minWidth: { md: 150 } }}>
              <InputLabel>Grade</InputLabel>
              <Select
                value={gradeFilter}
                onChange={(e) => setGradeFilter(e.target.value)}
                label="Grade"
              >
                <MenuItem value="all">All Grades</MenuItem>
                {[...Array(12)].map((_, i) => (
                  <MenuItem key={i + 1} value={(i + 1).toString()}>
                    Grade {i + 1}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ flex: { xs: 1, md: 'none' }, minWidth: { md: 150 } }}>
              <InputLabel>Pricing Tier</InputLabel>
              <Select
                value={tierFilter}
                onChange={(e) => setTierFilter(e.target.value)}
                label="Pricing Tier"
              >
                <MenuItem value="all">All Tiers</MenuItem>
                <MenuItem value="full">Full Price</MenuItem>
                <MenuItem value="reduced">Reduced</MenuItem>
                <MenuItem value="free">Free</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ flex: { xs: 1, md: 'none' }, minWidth: { md: 180 } }}>
              <InputLabel>School</InputLabel>
              <Select
                value={schoolFilter}
                onChange={(e) => setSchoolFilter(e.target.value)}
                label="School"
              >
                <MenuItem value="all">All Schools</MenuItem>
                {schools.map(school => (
                  <MenuItem key={school.id} value={school.id}>
                    {school.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Box>
      </Paper>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            md: 'repeat(2, 1fr)',
            lg: 'repeat(3, 1fr)',
          },
          gap: 3,
        }}
      >
        {filteredStudents.map((student) => (
          <Box
            key={student.id}
            sx={{
              display: 'flex',
              minWidth: 0,
            }}
          >
            <Card sx={{
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              minWidth: 0,
              overflow: 'hidden',
            }}>
              <CardContent sx={{
                flexGrow: 1,
                minWidth: 0,
                maxWidth: '100%',
              }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      variant="h6"
                      fontWeight={600}
                      sx={{
                        minWidth: 0,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {student.firstName} {student.lastName}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      ID: {student.studentIdNumber}
                    </Typography>
                  </Box>
                  <Chip
                    label={`Grade ${student.grade}`}
                    size="small"
                    sx={{
                      ml: 1,
                      bgcolor: '#e0f2fe',
                      color: '#0284c7',
                      fontWeight: 600,
                      flexShrink: 0,
                    }}
                  />
                </Box>

                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    mb: 2,
                    minWidth: 0,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {student.email}
                </Typography>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Typography variant="body2" fontWeight={500} color="text.secondary">
                    Pricing Tier:
                  </Typography>
                  <Chip
                    label={getPricingTierLabel(student.pricingTier)}
                    size="small"
                    sx={{
                      bgcolor: getPricingTierColor(student.pricingTier),
                      color: 'white',
                      fontWeight: 600,
                    }}
                  />
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" fontWeight={500} color="text.secondary">
                    School:
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {getSchoolName(student.schoolId)}
                  </Typography>
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" fontWeight={500} color="text.secondary" sx={{ mb: 0.5 }}>
                    Parents:
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      minWidth: 0,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {getParentNames(student.parentIds)}
                  </Typography>
                </Box>

                {(() => {
                  const preAssessedFees = getStudentPreAssessedFees(student.id);
                  if (preAssessedFees.length > 0) {
                    return (
                      <Box sx={{ borderTop: 1, borderColor: 'divider', pt: 2 }}>
                        <Button
                          size="small"
                          variant="text"
                          onClick={() => setViewingFeesForStudent(student)}
                          sx={{
                            textTransform: 'none',
                            color: 'primary.main',
                            p: 0,
                            minWidth: 0,
                            '&:hover': {
                              bgcolor: 'transparent',
                              textDecoration: 'underline',
                            },
                          }}
                        >
                          View Pre-Assessed Fees ({preAssessedFees.length})
                        </Button>
                      </Box>
                    );
                  }
                  return null;
                })()}
              </CardContent>

              <CardActions sx={{ px: 2, pb: 2, pt: 0 }}>
                {permissions.update && (
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<Edit />}
                    onClick={() => handleEdit(student)}
                    sx={{ flexGrow: 1, minWidth: 0 }}
                  >
                    Edit
                  </Button>
                )}
                {permissions.delete && (
                  <IconButton
                    size="small"
                    onClick={() => handleDelete(student.id)}
                    color="error"
                    sx={{ flexShrink: 0 }}
                  >
                    <Delete />
                  </IconButton>
                )}
              </CardActions>
            </Card>
          </Box>
        ))}
      </Box>

      {filteredStudents.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" color="text.secondary">
            No students found
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {searchQuery || gradeFilter !== 'all' || tierFilter !== 'all' || schoolFilter !== 'all'
              ? 'Try adjusting your filters'
              : 'Click "Add Student" to create your first student'}
          </Typography>
        </Box>
      )}

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingStudent ? 'Edit Student' : 'Add New Student'}
        </DialogTitle>
        <DialogContent dividers sx={{ maxHeight: '60vh', overflow: 'auto' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Student ID Number"
              value={formData.studentIdNumber}
              onChange={(e) => setFormData({ ...formData, studentIdNumber: e.target.value })}
              fullWidth
              placeholder="e.g., S2024001"
            />
            <TextField
              label="First Name"
              value={formData.firstName}
              onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
              fullWidth
            />
            <TextField
              label="Last Name"
              value={formData.lastName}
              onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
              fullWidth
            />
            <TextField
              label="Email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              fullWidth
            />
            <TextField
              label="Grade"
              type="number"
              value={formData.grade}
              onChange={(e) => setFormData({ ...formData, grade: parseInt(e.target.value) })}
              inputProps={{ min: 1, max: 12 }}
              fullWidth
            />
            <FormControl fullWidth>
              <InputLabel>Pricing Tier</InputLabel>
              <Select
                value={formData.pricingTier}
                onChange={(e) => setFormData({ ...formData, pricingTier: e.target.value as PricingTier })}
                label="Pricing Tier"
              >
                <MenuItem value="full">Full Price</MenuItem>
                <MenuItem value="reduced">Reduced Price</MenuItem>
                <MenuItem value="free">Free</MenuItem>
              </Select>
            </FormControl>
            <Autocomplete
              options={schools}
              getOptionLabel={(option: School) => `${option.name} (${option.code})`}
              value={schools.find(s => s.id === formData.schoolId) || null}
              onChange={(_, newValue) => {
                setFormData({ ...formData, schoolId: newValue?.id || '' });
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="School"
                  required
                  placeholder="Select school..."
                />
              )}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button
            onClick={handleSave}
            variant="contained"
            sx={{
              background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
              },
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
